import type { SolutionsContent } from "./solutions";

export const defaultSolutionsContent: SolutionsContent = {
  meta: {
    title: "Quintara Solutions",
    description: "WhatsApp business messaging for enterprises, governments, agencies, mobile operators and SMBs.",
  },
  hero: {
    eyebrow: "Solutions",
    heading: "One platform. Four ways to bring business to WhatsApp.",
    lede: "How we work depends on who you are. Enterprises get dedicated teams, agencies get tools for their whole client book, operators get a new product line, and small businesses get a guided setup they can finish on their own.",
  },
  specs: [
    {
      id: "enterprise",
      eyebrow: "Enterprise and government",
      heading: "Critical messages, delivered with care.",
      intro: "For banks, retailers, airlines, utilities and public bodies that send at scale and can't afford a broken journey.",
      items: [
        { title: "Dedicated onboarding", description: "A named commercial lead and solutions engineer take you from business verification to your first live templates." },
        { title: "Authentication and alerts", description: "Move one-time passcodes and service notifications off SMS, with fallback rules you control." },
        { title: "Citizen services", description: "Templates and compliance patterns for public-sector messaging, from appointment reminders to service updates." },
        { title: "In-region data", description: "Hosting in the UAE and Bahrain, designed around UAE and Saudi personal data protection laws." },
        { title: "CRM and contact center", description: "Native Salesforce, HubSpot and Zoho connectors, plus a REST API and webhooks for your own systems." },
        { title: "Enterprise support", description: "Round-the-clock on-call engineering, with a 15-minute response target for critical incidents." },
      ],
    },
    {
      id: "agencies",
      eyebrow: "Agencies",
      heading: "Make messaging part of the media plan.",
      intro: "For holding groups and independent agencies who want WhatsApp to carry the same accountability as paid media.",
      items: [
        { title: "Click-to-WhatsApp campaigns", description: "Ads that open a conversation, with the ad referral captured and carried through to the sale." },
        { title: "Conversions API", description: "Server-side events with deduplication and match-quality monitoring, so performance is measured properly." },
        { title: "One setup, many clients", description: "Guided onboarding you repeat across your advertiser book, with separate accounts for each brand." },
        { title: "Reporting your clients read", description: "Cost per conversation, template performance and response times, by brand and by campaign." },
      ],
    },
    {
      id: "operators",
      eyebrow: "Mobile operators",
      heading: "A new product for your business customers.",
      intro: "For operators who want to keep their A2P customers as messaging moves to richer channels.",
      items: [
        { title: "SMS-to-WhatsApp migration", description: "A repeatable path to move enterprise A2P traffic onto WhatsApp, use case by use case." },
        { title: "White-label resale", description: "Offer WhatsApp business messaging to your enterprise and SME base under your commercial relationship." },
        { title: "Carrier billing", description: "Let small businesses pay through the operator account they already have." },
        { title: "Fraud protection", description: "Rate limits and SMS-pumping protections drawn from years of operator-side work." },
      ],
    },
    {
      id: "smb",
      eyebrow: "Small and medium businesses",
      heading: "Set up in an afternoon.",
      intro: "For shops, restaurants, clinics and service businesses that want WhatsApp to bring in and keep customers.",
      items: [
        { title: "Guided setup", description: "A conversational flow walks you through verification, display name, number registration and your first template, and checks them against WhatsApp policies before you submit." },
        { title: "Store connectors", description: "Salla, Zid and Shopify connectors for catalog sync, abandoned carts, order confirmations and shipping updates." },
        { title: "Website button", description: "A small snippet adds a click-to-WhatsApp button to your site, with no developer needed." },
        { title: "AI replies", description: "An assistant trained on your menu, prices and FAQs answers routine questions around the clock." },
      ],
    },
  ],
  industries: {
    eyebrow: "Industries",
    heading: "Where WhatsApp moves the numbers.",
    lede: "We focus on sectors where conversations are frequent or where one lead is worth a lot.",
    tags: [
      "E-commerce and retail",
      "Restaurants and food delivery",
      "Automotive",
      "Real estate",
      "Healthcare",
      "Telecom",
      "Hospitality",
      "Airlines",
      "Government",
    ],
  },
  ctaBand: {
    eyebrow: "Get in touch",
    heading: "Tell us what you send today.",
    lede: "Share your current SMS and messaging volumes and we'll map out which use cases to move first.",
    buttonLabel: "Talk to our team",
    buttonTo: "/contact",
  },
};
